/**
 * BirFlyPermit — shared domain types.
 * This file is the contract between the web app, the backend core and every adapter.
 * Keep it dependency-free. Dates are ISO strings (YYYY-MM-DD); timestamps are ISO 8601 UTC.
 */

export type ISODate = string; // 'YYYY-MM-DD'
export type ISOTimestamp = string; // '2026-09-24T10:00:00.000Z'

// ── Workflow ────────────────────────────────────────────────────────────────

export const APPLICATION_STATES = [
  'Submitted',
  'Validated',
  'CorrectionRequested',
  'ReadyForEvaluation',
  'Evaluated',
  'EvaluationFailed',
  'UnderReview',
  'PaymentPending',
  'Approved',
  'CardIssued',
  'Rejected',
  'Revoked',
  'Expired',
] as const;
export type ApplicationState = (typeof APPLICATION_STATES)[number];

export const ROLES = ['pilot', 'evaluator', 'assistant', 'sdm', 'dc', 'responder', 'admin', 'verifier'] as const;
export type Role = (typeof ROLES)[number];

export const CASE_ACTIONS = [
  'assign',
  'note',
  'requestCorrection',
  'evaluate',
  'recommend',
  'approve',
  'reject',
  'revoke',
  'reissue',
  'markPrinted',
  'override',
  'reopen',
  'confirmPayment',
  'waivePayment',
  'resendPaymentLink',
  'rejectPaymentClaim',
] as const;
export type CaseActionName = (typeof CASE_ACTIONS)[number];

// ── Permit fee (feedback round 2) ───────────────────────────────────────────
//
// `none`     — no fee: approve goes straight to Approved (the pre-October behaviour).
// `upi`      — zero-cost manual route: the pilot pays the office's UPI id (deep link / QR with the
//              exact amount and the application reference), reports the UTR, and an officer
//              confirms against the bank statement. Indian pilots only (UPI needs an Indian bank).
// `razorpay` — hosted Razorpay Payment Link (UPI, Indian cards, international cards); confirmed by
//              the signed redirect, the gateway webhook, or a server-side status fetch — never by
//              the pilot's word alone.
// `office`   — pay at the office counter / by bank transfer as instructed (`payment_instructions`
//              config); the pilot reports the receipt number and an officer confirms. Lets the fee
//              stage run before a UPI id or gateway account exists.
//
// Manual routes (`upi`, `office`) — round 4, 16 Oct 2026: the pilot is shown every way the office
// accepts money (UPI QR / deep link, bank account for NEFT/IMPS, counter instructions) and proves
// payment with a UTR and/or a screenshot plus the amount, date and payer name. An officer matches
// the credit in the office's own bank/UPI app — never the screenshot — then `confirmPayment`, or
// sends it back with `rejectPaymentClaim` (reason e-mailed; the pilot may re-submit).
export const PAYMENT_PROVIDERS = ['none', 'upi', 'razorpay', 'office'] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

export type PaymentStatus =
  | 'pending' // link / UPI details sent, nothing received yet (also after a claim was sent back)
  | 'claimed' // the pilot reported a UTR / screenshot — an officer must confirm it
  | 'paid' // confirmed (gateway or officer)
  | 'waived' // SDM waived the fee
  | 'expired'; // gateway link expired or was cancelled — resendPaymentLink creates a new one

export interface PaymentRecord {
  provider: Exclude<PaymentProvider, 'none'>;
  amountInr: number; // whole rupees
  /** Per-week rule: ₹unitInr × weeks (weeks = ceil(stay days / 7), min 1). Absent for a flat fee. */
  weeks?: number;
  unitInr?: number;
  /** upi + office routes: what the pilot was told to do (counter hours, who to call, …). */
  instructions?: string;
  /** upi + office routes: the office bank account for NEFT / IMPS / RTGS, when configured. */
  bank?: BankDetails;
  status: PaymentStatus;
  requestedAt: ISOTimestamp;
  dueBy?: ISODate;
  // razorpay
  linkId?: string; // plink_…
  linkUrl?: string; // the hosted short_url the pilot opens
  // upi
  upiVpa?: string;
  upiPayeeName?: string;
  /** The exact text of the office's printed (bank-issued) UPI QR, when configured — the pilot's QR and deep link are built on it. */
  upiQrPayload?: string;
  // outcome
  reference?: string; // gateway payment id, or the UTR the officer matched
  method?: string; // upi / card / netbanking … as reported by the gateway, or the pilot's claim method
  paidAt?: ISOTimestamp;
  /** Manual routes: the amount the officer actually found credited (defaults to amountInr). */
  paidAmountInr?: number;
  confirmedBy?: string; // staff user, or 'razorpay'
  confirmedVia?: 'callback' | 'webhook' | 'poll' | 'manual' | 'waiver';
  /** What the pilot reported from the status page (manual routes, or a gateway fallback). */
  claim?: PaymentClaim | null;
  /** Claims an officer sent back with `rejectPaymentClaim`, oldest first. */
  rejectedClaims?: RejectedPaymentClaim[];
  linkSentAt?: ISOTimestamp;
  linkSentCount?: number;
  note?: string;
}

/** How the pilot says they paid on a manual route. */
export const PAYMENT_CLAIM_METHODS = ['upi', 'bank', 'cash', 'other'] as const;
export type PaymentClaimMethod = (typeof PAYMENT_CLAIM_METHODS)[number];

/** Indemnity bond clauses the pilot must accept: I–VII from the office's printed bond, VIII = the no-fly areas the reviewer added on 14 Oct 2026. */
export const BOND_CLAUSE_COUNT = 8;

export interface PaymentClaim {
  /** UTR / bank reference / transaction id. Optional when a screenshot is attached. */
  reference?: string;
  method?: PaymentClaimMethod;
  /** Amount the pilot says they paid, whole rupees. */
  amountInr?: number;
  /** Date of the transfer as shown in the pilot's app. */
  paidOn?: ISODate;
  /** Name on the paying account — often not the pilot (a friend's phone, a parent's account). */
  payerName?: string;
  note: string;
  at: ISOTimestamp;
  /** Screenshot / PDF in the `payment` file slot. */
  fileId?: string;
}

export interface RejectedPaymentClaim extends PaymentClaim {
  rejectedAt: ISOTimestamp;
  rejectedBy: string; // staff user
  rejectedReason: string;
}

export interface BankDetails {
  accountName: string;
  accountNumber: string;
  ifsc: string;
  bankName: string; // bank + branch, free text
}

export type RiskTier = 'green' | 'amber' | 'red';
export type Severity = 'pass' | 'warn' | 'fail';

// ── Validation ──────────────────────────────────────────────────────────────

export interface Finding {
  rule: string; // e.g. 'mrz.checkdigits'
  severity: Severity;
  expected?: string;
  found?: string;
  evidence?: string;
  explanation: string; // plain language, shown to pilot and officer
  overridden?: { by: string; reason: string; at: ISOTimestamp };
}

export interface MrzResult {
  ok: boolean;
  reason?: string;
  checks: { docNo: boolean; dob: boolean; expiry: boolean; composite: boolean };
  documentNumber: string;
  issuingCountry: string;
  nationality: string;
  surname: string;
  givenNames: string;
  sex: string;
  dateOfBirth: ISODate;
  dateOfExpiry: ISODate;
  personalNumber: string;
  line1: string;
  line2: string;
}

// ── Application fields (what the pilot fills) ───────────────────────────────

export type DocType = 'passport' | 'aadhaar' | 'voter_id' | 'driving_licence' | 'other';
export type GliderClass = 'EN-A' | 'EN-B' | 'EN-C' | 'EN-D' | 'CCC' | 'DHV-1' | 'DHV-1-2' | 'DHV-2' | 'DHV-2-3' | 'DHV-3' | 'unknown';

export interface PilotFields {
  givenNames: string;
  surname: string;
  fathersName: string;
  addressLine: string;
  city: string;
  region: string;
  country: string; // ISO 3166-1 alpha-3
  postcode: string;
  nationality: string; // ISO 3166-1 alpha-3
  dob: ISODate;
  phone: string; // E.164
  email: string;

  docType: DocType;
  docNumber: string;
  docIssuingCountry: string;
  docExpiry: ISODate | '';
  mrzLine1: string;
  mrzLine2: string;

  isForeign: boolean;
  arrivalDate: ISODate | '';
  visaType: string;
  visaValidFrom: ISODate | '';
  visaValidTo: ISODate | '';

  certBody: string;
  certLevel: string;
  certNumber: string;
  certIssued: ISODate | '';
  certExpiry: ISODate | '';
  certHolderName: string;

  gliderMake: string;
  gliderModel: string;
  gliderSize: string;
  gliderClass: GliderClass;
  reserveMake: string;
  reserveModel: string;
  reserveRepack: ISODate | '';

  insProvider: string;
  insPolicyNo: string;
  insInsuredName: string;
  insCoverText: string; // free text or OCR of the cover wording; searched for cover phrases
  insValidTo: ISODate | '';
  medicalValidTo: ISODate | '';

  emgName: string;
  emgRelationship: string;
  emgPhone: string;
  bloodGroup: string;

  // Stay in Bir and rescue radio (feedback round 2). The stay window drives the card validity and
  // the per-week permit fee (feedback round 3).
  stayFrom: ISODate;
  stayTo: ISODate;
  stayPlace: string;
  stayArea: string;
  radioFrequency: string;

  declarationAccepted: boolean[]; // 4 clauses
  bondAccepted: boolean[]; // BOND_CLAUSE_COUNT clauses (8 since 17 Oct 2026: VIII = no-fly areas)
  typedName: string;
  consentPurpose: boolean;
}

export type FileSlot = 'doc' | 'photo' | 'cert' | 'ins' | 'signed' | 'card' | 'payment';

export interface UploadFile {
  slot: FileSlot;
  name: string;
  mime: string;
  data: string; // base64 (no data: prefix)
  sha256?: string;
}

/**
 * One stored page/file of a slot. A slot such as `ins` (a multi-page insurance policy),
 * `doc` or `cert` may hold several files; `photo` always holds exactly one.
 * `Application.files[slot]` is the FIRST file's id (kept for every reader that expects one id
 * per slot); `Application.filePages[slot]` lists every file of the slot in upload order.
 */
export interface FilePage {
  fileId: string;
  name: string;
  mime: string;
}

// ── Stored application (one row of the register) ────────────────────────────

export interface Application {
  ref: string; // A-<season>-<8 hex>
  status: ApplicationState;
  season: string;
  createdAt: ISOTimestamp;
  updatedAt: ISOTimestamp;
  idemKey: string;
  statusTokenHash: string;

  fields: PilotFields;
  mrz: MrzResult | null;

  findings: Finding[];
  riskTier: RiskTier;
  rulesetVersion: string;

  assignedTo?: string;
  evaluatorUser?: string;
  assessmentOutcome?: 'pass' | 'fail' | 'conditional';
  assessmentNotes?: string;
  assessmentChecklist?: Record<string, boolean>;
  assessedAt?: ISOTimestamp;

  reviewerUser?: string;
  recommendation?: 'approve' | 'reject';
  reviewedAt?: ISOTimestamp;

  approverUser?: string;
  decision?: 'approved' | 'rejected';
  decisionReason?: string;
  decidedAt?: ISOTimestamp;

  correctionRequest?: { items: string[]; message: string; requestedBy: string; at: ISOTimestamp } | null;
  notes?: { by: string; at: ISOTimestamp; text: string }[];

  /** The permit fee, once approve has asked for one (null/absent = no fee was ever due). */
  payment?: PaymentRecord | null;
  /** Hashes of the status-page tokens minted with each payment e-mail (newest last, at most three kept),
   *  so an older payment e-mail — and the gateway callback URL built from it — keeps working after a re-send. */
  paymentTokenHashes?: string[];

  card?: CardRecord | null;
  files: Partial<Record<FileSlot, string>>; // slot → FIRST file id (see FilePage)
  filePages?: Partial<Record<FileSlot, FilePage[]>>; // slot → every file, in upload order
  driveFolderId?: string;
  photoSha256?: string;
}

export interface CardRecord {
  cardNo: string; // BB-2026-000123
  validFrom: ISODate;
  validTo: ISODate;
  keyId: string;
  payload: string; // the exact signed string (without signature)
  signature: string; // base64url
  photoHash12: string;
  issuedAt: ISOTimestamp;
  printedAt?: ISOTimestamp;
  printSheet?: string;
  revokedAt?: ISOTimestamp;
  revocationReason?: string;
  reissuedFrom?: string;
}

export interface AuditRow {
  seq: number;
  at: ISOTimestamp;
  actor: string;
  role: Role | 'system';
  action: string;
  entity: 'application' | 'card' | 'staff' | 'config' | 'system';
  entityId: string;
  fromState: string;
  toState: string;
  reason: string;
}

export interface Staff {
  user: string;
  displayName: string;
  role: Role;
  active: boolean;
  notes?: string;
}

export interface ScanRow {
  at: ISOTimestamp;
  cardNo: string;
  deviceId: string;
  result: 'green' | 'amber' | 'red';
  reason: string;
  offline: boolean;
  listAgeSeconds: number;
  lat?: number;
  lng?: number;
}

export interface RevocationList {
  version: number;
  generatedAt: ISOTimestamp;
  keyId: string;
  revoked: { cardNo: string; at: ISOTimestamp; reason?: string }[];
  signature: string; // Ed25519 over the canonical JSON of {version, generatedAt, keyId, revoked}
}

// ── Sessions ────────────────────────────────────────────────────────────────

export interface Session {
  user: string;
  role: Role;
  displayName: string;
  exp: number; // ms epoch
}

// ── Tenant configuration ────────────────────────────────────────────────────

export interface TenantKey {
  kid: string;
  publicKey: string; // base64url, 32 bytes
  validFrom: ISODate;
  validTo: ISODate;
  note?: string;
}

export interface TenantConfig {
  tenantId: string;
  office: {
    name: string;
    shortName: string;
    district: string;
    state: string;
    site: string;
    siteCode: string;
    issuingAuthority: string;
    address: string;
    phone: string;
    email: string;
    whatsapp: string;
    grievanceOfficer: string;
    emblem: string;
  };
  product: {
    name: string;
    cardName: string;
    tagline: string;
    publicUrl: string;
    languages: string[];
    defaultLanguage: string;
  };
  season: {
    key: string;
    label: string;
    validFrom: ISODate;
    validTo: ISODate;
    cardValidityDays: number; // 0 = season end
    cardPrefix: string;
  };
  theme: { primary: string; primaryStrong: string; accent: string; ink: string };
  rules: {
    rulesetVersion: string;
    nameMatchPass: number;
    nameMatchWarn: number;
    recognisedCertBodies: string[];
    certLevels: string[];
    gliderClassMaxByLevel: Record<string, string>;
    insuranceCoverPhrases: string[];
  };
  keys: TenantKey[];
  retention: { applicationYears: number; documentYearsAfterExpiry: number; scanLogYears: number };
  verify: { revocationListPath: string; staleAfterHours: number };
}

// ── Dashboard aggregates ────────────────────────────────────────────────────

export interface StatsSnapshot {
  generatedAt: ISOTimestamp;
  season: string;
  totals: {
    applications: number;
    byState: Record<ApplicationState, number>;
    cardsIssued: number;
    cardsActive: number;
    cardsRevoked: number;
    indian: number;
    foreign: number;
  };
  byNationality: { nationality: string; count: number }[];
  byDay: { date: ISODate; submissions: number; issued: number }[];
  byTier: Record<RiskTier, number>;
  byEvaluator: { user: string; count: number; pass: number; fail: number }[];
  queueAges: { state: ApplicationState; count: number; oldestHours: number }[];
  expiringIn7Days: number;
  avgApprovalHours: number | null;
  topFindings: { rule: string; count: number }[];
}
